import Button from "../../atoms/button";
import Checkbox from "../../atoms/checkbox";
import { vw } from "../../../lib/utils/responsiveUtils";
import * as React from "react";
import { useFormContext } from "react-hook-form";
import type { getResveTimeDetailGolfParkForm } from "../../../@apis/dto/request/resve.request";
import { useGetResveTimeDetail } from "../../../@apis/hooks/useResve";
import { useQuery } from "@tanstack/react-query";
import { formatKoDayDate } from "../../../lib/utils/dateUtils";
import { staticURL } from "../../../lib/utils/imageUtils";
import { useMyInfo } from "../../../@apis/hooks/useAuth";
import { useAccessStore } from "../../../lib/store/useAccessStore";
import { useModalStore } from "../../../lib/store/useModalStore";

type CourseScreenProps = {
  golfParkCode?: string;
  date?: string;
};

const TERMS_INFO = [
  { label: "파크골프장 규칙 및 취소/환불 규정 동의", terms: `<div style="padding: 20px; border: 1px solid #ddd; border-radius: 8px; font-family: Arial, sans-serif; line-height: 1.6;">
    <h2 style="color: #333; border-bottom: 2px solid #eee; padding-bottom: 10px; margin-bottom: 20px;">결제 행위 및 취소/환불 규정 동의 (본문)</h2>
    <div class="agreement-content">
        <h3 style="color: #cc0000; margin-top: 0;">[필수] 예약 취소 및 환불 규정 동의</h3>
        <p>본 서비스는 이용일 기준에 따라 다음과 같은 취소 위약금을 적용하고 있습니다. 내용을 충분히 숙지하였으며 결제 진행에 동의합니다.</p>

        <h4 style="color: #555; margin-top: 20px; border-left: 4px solid #007bff; padding-left: 10px;">1. 취소/환불 규정</h4>

        <h5 style="color: #111; margin-top: 15px; margin-bottom: 5px;">[주말/공휴일 예약]</h5>
        <ul style="list-style-type: disc; padding-left: 20px; margin-bottom: 15px;">
            <li style="margin-bottom: 5px;">이용 4일 전까지: <strong>위약금 없이 전액 환불</strong></li>
            <li style="margin-bottom: 5px;">이용 3일 전 ~ 2일 전: 이용요금의 <strong>10% 위약금 공제 후 환불</strong></li>
            <li style="margin-bottom: 5px;">이용 1일 전: 이용요금의 <strong>20% 위약금 공제 후 환불</strong></li>
            <li style="margin-bottom: 5px;">이용 당일: 이용요금의 <strong>30% 위약금 공제 후 환불</strong></li>
        </ul>

        <h5 style="color: #111; margin-top: 15px; margin-bottom: 5px;">[평일 예약]</h5>
        <ul style="list-style-type: disc; padding-left: 20px; margin-bottom: 15px;">
            <li style="margin-bottom: 5px;">이용 3일 전까지: <strong>위약금 없이 전액 환불</strong></li>
            <li style="margin-bottom: 5px;">이용 2일 전: 이용요금의 <strong>10% 위약금 공제 후 환불</strong></li>
            <li style="margin-bottom: 5px;">이용 1일 전: 이용요금의 <strong>20% 위약금 공제 후 환불</strong></li>
            <li style="margin-bottom: 5px;">이용 당일: 이용요금의 <strong>30% 위약금 공제 후 환불</strong></li>
        </ul>

        <h4 style="color: #555; margin-top: 20px; border-left: 4px solid #007bff; padding-left: 10px;">2. 예외 규정 (천재지변 등)</h4>
        <p>폭우, 폭설, 안개 등 골프장 측에서 정상적인 라운딩이 불가능하다고 판단하여 취소를 결정한 경우, 위약금 없이 전액 환불 또는 홀별 정산이 가능합니다. 단, 개인적 판단에 의한 취소는 일반 규정이 적용됩니다.</p>

        <h4 style="color: #555; margin-top: 20px; border-left: 4px solid #007bff; padding-left: 10px;">3. 기타</h4>
        <p>환불은 결제한 수단과 동일한 방법으로 진행되나, 카드사 사정에 따라 3~7일 정도 소요될 수 있습니다.</p>
    </div>
</div>`},
  // "개인정보 수집 및 활용 동의서 동의",
  // "개인정보 제 3자 제공 동의",
  // "결제 대행 서비스 약관 동의",
];

export default function PaymentScreen({ golfParkCode, date }: CourseScreenProps) {

  const { watch, setValue } = useFormContext<{
    selectedCourse?: string;
    allAgreed?: boolean;
    memberCount: number;
  }>();
  const selectedCourse = watch("selectedCourse");
  const memberCount = watch("memberCount") || 1;

  const propsForm: getResveTimeDetailGolfParkForm = {
    golfParkCode: golfParkCode ?? "",
    date: date ?? "",
    resveSeq: Number(selectedCourse || 0),
  };

  const { data: resveTimeDetailResponse } = useQuery({
    ...useGetResveTimeDetail(propsForm),
    enabled: !!propsForm.golfParkCode && !!propsForm.date && !!selectedCourse,
  });
  const timeDetailInfo = resveTimeDetailResponse?.data?.resveInfo;
  const resveTimeMemberList = timeDetailInfo?.resveTimeMemberList || [];

  const { access } = useAccessStore();
  const { openModal } = useModalStore();
  const { data: myInfoResponse } = useQuery({
    ...useMyInfo(),
    enabled: !!access.accessToken,
  });
  const myinfo = myInfoResponse?.data;

  const Z_CLASSES = ["z-40", "z-30", "z-20", "z-10"] as const;
  
  // 개별 약관 상태 (4개 모두 false로 시작)
  const [agreements, setAgreements] = React.useState<boolean[]>([false]);

  // 전체 동의 여부
  const allAgreed = agreements.every(Boolean);

  // 전체 동의 버튼 클릭 시
  const handleAllAgree = () => {
    if (allAgreed) return;
    setAgreements([true]);
  };

  // 개별 체크박스 클릭 시
  const handleSingleAgree = (index: number, term: string) => {
    const next = [...agreements];
    next[index] = !next[index];
    if (agreements[index] === false) {
      openModal("TERMS_MODAL", { title: "약관", html: term });
    }
    setAgreements(next);
  };

  React.useEffect(() => {
    setValue("allAgreed", allAgreed);    
  }, [allAgreed, setValue]);



  return (
    <div className="px-[15px] flex-col justify-between">
    
      <div>
        {/* 날짜 영역 */}
        <div className="rounded-[15px] bg-[#f5f5f5] p-[20px] flex items-center" style={{ height: vw(60) }}>
          <p className="font-bold text-[#222]" style={{ fontSize: vw(16) }}>
            {formatKoDayDate(date ?? "")} {timeDetailInfo?.startTime}
          </p>
        </div>

        <div className="mx-auto" style={{ width: vw(315), height: "1px", backgroundImage: "repeating-linear-gradient(to right,#e6e6e6 0 3px,transparent 6px 5px)", borderRadius: "2px", }} />

        {/* 내용 영역 */}
        <div className="rounded-[15px] bg-[#f5f5f5] p-[20px] flex items-center" style={{ height: vw(141) }}>
          <div className="flex items-start gap-[19px]">
            <img
              src={
                timeDetailInfo?.golfParkImageUrl
                  ? staticURL(timeDetailInfo?.golfParkImageUrl)
                  : "/images/img-login-logo.webp"
              }
              alt="골프장 이미지"
              className="rounded-[10px] object-cover bg-white"
              style={{ width: vw(83), height: vw(83) }}
            />
            <div className="flex flex-col justify-start">
              <p className="text-[#222] font-bold line-clamp-1" style={{ fontSize: vw(20) }}>
                {timeDetailInfo?.golfParkName}
              </p>
              <div className="mt-[18px] pl-[15px] border-l-[2px] border-[#e6e6e6]">
                <p
                  className="text-[14px] text-[#333] font-[500]"
                  style={{ fontSize: vw(14) }}
                >
                  {timeDetailInfo?.golfCourseName}
                </p>
                {/* 참가자 아이콘 */}
                <div className="mt-[9px] flex items-center -space-x-[10px]">
                  {(() => {
                    const icons: { bg: string; icon: string; mine: boolean }[] = [];

                    // 1) 기존 예약자들 먼저
                    resveTimeMemberList.slice(0, 4).forEach((m) => {
                      const bg = m.sex === 0 ? "#f95e80" :  m.sex === 1 ? "#25a1d5" : "#e6e6e6";             
                      const icon =
                        m.sex === 0
                          ? "/icons/ic-female.webp"
                          : m.sex === 1
                          ? "/icons/ic-male.webp"
                          : "/icons/ic-gender.webp";

                      icons.push({ bg, icon, mine: false });
                    });

                    // 2) 나 
                    if (icons.length < 4 && myinfo) {
                      const sex = Number(myinfo.gender); 
                      const bg = sex === 0 ? "#f95e80" : sex === 1 ? "#25a1d5" : "#e6e6e6";
                      const icon =
                        sex === 0
                          ? "/icons/ic-female.webp"
                          : sex === 1
                          ? "/icons/ic-male.webp"
                          : "/icons/ic-gender.webp";
                      icons.push({ bg, icon, mine: true });
                    }

                    // 3) 동반자들: memberCount 기준으로 개수 계산
                    const companionCount = Math.max(0, memberCount - 1);
                    const availableSlots = 4 - icons.length;
                    const companionsToShow = Math.min(companionCount, availableSlots);

                    for (let i = 0; i < companionsToShow; i++) {
                      icons.push({
                        bg: "#e6e6e6",             
                        icon: "/icons/ic-gender.webp", 
                        mine: true,               
                      });
                    }

                    // 4) 남은 자리
                    while (icons.length < 4) {
                      icons.push({
                        bg: "#e6e6e6",
                        icon: "/icons/ic-rest.webp",
                        mine: false,
                      });
                    }

                    return icons.map((p, idx) => (
                      <div
                        key={idx}
                        className={`${Z_CLASSES[idx]} rounded-full flex justify-center items-center border-2 ${
                          p.mine ? "border-[#222]" : "border-white"
                        }`}
                        style={{ width: vw(40), height: vw(40), backgroundColor: p.bg }}
                      >
                        <img
                          src={p.icon}
                          style={{ width: vw(26), height: vw(24) }}
                        />
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-[15px]">
        <div className="rounded-[15px] bg-[#f5f5f5] p-[20px] flex items-center justify-between" style={{ height: vw(58) }}>
            <p className="text-[16px] text-[#222] font-bold">라운드 비용</p>
            <p className="text-[#e5002b] font-bold" style={{ fontSize: vw(20) }}>
              {memberCount}인{" "} 
              {(memberCount * (timeDetailInfo?.greenfee ?? 0)).toLocaleString()}원
            </p>
        </div>
      </div>

      {/* 전체 약관 동의 버튼 */}
      <div className="mt-[15px]">
        <Button
          className="w-full font-bold"
          style={{ fontSize: vw(16), height: vw(50) }}
          variant={allAgreed ? "selected" : "white"}  
          label={"전체 약관 동의하기"}
          onClick={handleAllAgree}
        />
      </div>

      {/* 개별 약관 */}
      <div className="mt-[10px]">
        {TERMS_INFO.map(({label, terms}, i) => (
          <div
            key={i}
            className="flex-row-between py-[15px] border-b border-[#e6e6e6] cursor-pointer select-none"
            role="button"
            tabIndex={0}
            onClick={() => handleSingleAgree(i, terms)}
          >
            <Checkbox label={label} checked={agreements[i]} onChange={() => handleSingleAgree(i, terms)} />
            <img className="w-[6px] h-[14px]" src="/icons/ic-go.webp" alt=">" />
          </div>
        ))}
      </div>

    </div>
  );
}